import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Container,
    CircularProgress,
    Alert,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getLatestRates, getCountryFromCoordinates } from '../services/exchangeRateService';
import { TOP_CURRENCIES, CURRENCY_TO_COUNTRY, COUNTRY_TO_CURRENCY } from '../constants';
import CurrencyExchangeForm from '../components/currency/CurrencyExchangeForm';
import CurrentRatesTable from '../components/currency/CurrentRatesTable';
import ReverseRatesTable from '../components/currency/ReverseRatesTable';
import CurrencySearch from '../components/currency/CurrencySearch';

const GuestDashboard: React.FC = () => {
    const { t } = useTranslation();
    const [amount, setAmount] = useState<string>('1');
    const [fromCurrency, setFromCurrency] = useState<string>('USD');
    const [toCurrency, setToCurrency] = useState<string>('USD');
    const [convertedAmount, setConvertedAmount] = useState<number | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [exchangeRates, setExchangeRates] = useState<{ [key: string]: number }>({});
    const [customRates, setCustomRates] = useState<{ [key: string]: number }>({});
    const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [availableCurrencies] = useState<string[]>(Object.keys(CURRENCY_TO_COUNTRY));
    const [selectedCurrencies, setSelectedCurrencies] = useState<string[]>(TOP_CURRENCIES);
    const [searchQuery, setSearchQuery] = useState('');
    const [locationDetected, setLocationDetected] = useState(false);

    // Detect user location and set appropriate currency
    const detectUserLocation = useCallback(async () => {
        try {
            if (!navigator.geolocation) {
                console.log('Geolocation not supported, using default currency');
                return;
            }

            const position = await new Promise<GeolocationPosition>((resolve, reject) => {
                navigator.geolocation.getCurrentPosition(resolve, reject, {
                    enableHighAccuracy: true,
                    timeout: 10000,
                    maximumAge: 600000, // 10 minutes
                });
            });

            const { latitude, longitude } = position.coords;
            const countryCode = await getCountryFromCoordinates(latitude, longitude);
            const locationCurrency = COUNTRY_TO_CURRENCY[countryCode];

            if (locationCurrency && availableCurrencies.includes(locationCurrency)) {
                setToCurrency(locationCurrency);
                setLocationDetected(true);
                console.log(`Location detected: ${countryCode}, currency set to: ${locationCurrency}`);
            } else {
                console.log(`No currency mapping found for country: ${countryCode}, using default`);
            }
        } catch (error) {
            console.log('Location detection failed, using default currency:', error);
        }
    }, [availableCurrencies]);

    // Fetch exchange rates
    const fetchExchangeRates = useCallback(async (baseCurrency: string) => {
        setIsRefreshing(true);
        try {
            const response = await getLatestRates(baseCurrency);
            setExchangeRates(response.conversion_rates);
            setLastUpdated(new Date());
            setError(null);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to fetch exchange rates';
            setError(errorMessage);
        } finally {
            setIsRefreshing(false);
        }
    }, []);

    // Initial load with location detection
    useEffect(() => {
        const initializeApp = async () => {
            try {
                // First detect location
                await detectUserLocation();

                // Then fetch exchange rates
                await fetchExchangeRates('USD');
                setLoading(false);
            } catch (error) {
                setError('Failed to initialize exchange rates');
                setLoading(false);
            }
        };

        initializeApp();
    }, [detectUserLocation, fetchExchangeRates]);

    // Handle FROM currency change
    const handleFromCurrencyChange = useCallback((newCurrency: string) => {
        // Reset amount to 1 when FROM currency changes
        setAmount('1');
        setFromCurrency(newCurrency);
        setConvertedAmount(null);
        fetchExchangeRates(newCurrency);
    }, [fetchExchangeRates]);

    // Handle TO currency change
    const handleToCurrencyChange = useCallback((newCurrency: string) => {
        // Reset amount to 1 when TO currency changes
        setAmount('1');
        setToCurrency(newCurrency);
        setConvertedAmount(null);
    }, []);

    // Handle manual refresh
    const handleManualRefresh = () => {
        fetchExchangeRates(fromCurrency);
    };

    // Handle rate override
    const handleRateOverride = useCallback((rate: number) => {
        setCustomRates(prev => ({
            ...prev,
            [toCurrency]: rate
        }));
    }, [toCurrency]);

    // Get effective rate (custom or API)
    const getEffectiveRates = useCallback(() => {
        return {
            ...exchangeRates,
            ...customRates
        };
    }, [exchangeRates, customRates]);

    // Calculate conversion
    const handleCalculate = useCallback(() => {
        if (!amount || isNaN(Number(amount))) {
            setConvertedAmount(null);
            return;
        }

        try {
            const effectiveRates = getEffectiveRates();
            const rate = effectiveRates[toCurrency];
            if (!rate) {
                setError('Exchange rate not available');
                return;
            }

            const converted = Number(amount) * rate;
            const formattedAmount = toCurrency === 'IRR'
                ? Math.round(converted)
                : converted;

            setConvertedAmount(formattedAmount);
            setError(null);
        } catch (err) {
            setError('Error calculating conversion');
            console.error('Calculation error:', err);
        }
    }, [amount, toCurrency, getEffectiveRates]);

    // Auto-calculate when amount changes
    useEffect(() => {
        handleCalculate();
    }, [amount, handleCalculate]);

    // Handle swap currencies
    const handleSwapCurrencies = useCallback(() => {
        // Swap currencies
        const newFromCurrency = toCurrency;
        const newToCurrency = fromCurrency;

        // Swap amounts: the converted amount becomes the new input amount
        // Handle very small numbers (like IRR conversions) with proper precision
        let newAmount = amount;
        if (convertedAmount !== null) {
            if (toCurrency === 'IRR') {
                // IRR amounts are large whole numbers
                newAmount = Math.round(convertedAmount).toString();
            } else if (fromCurrency === 'IRR') {
                // Converting FROM IRR results in very small numbers
                // Use toFixed with enough precision to avoid scientific notation
                newAmount = convertedAmount.toFixed(10).replace(/\.?0+$/, '');
            } else {
                // Regular conversions
                newAmount = convertedAmount.toFixed(6).replace(/\.?0+$/, '');
            }
        }

        setFromCurrency(newFromCurrency);
        setToCurrency(newToCurrency);
        setAmount(newAmount);
        setConvertedAmount(null);

        // CRITICAL: Fetch new exchange rates for the new base currency
        // Without this, we're using old rates and calculations will be wrong!
        fetchExchangeRates(newFromCurrency);
    }, [fromCurrency, toCurrency, amount, convertedAmount, fetchExchangeRates]);

    // Add currency to selected list
    const handleAddCurrency = (currency: string) => {
        if (!selectedCurrencies.includes(currency)) {
            setSelectedCurrencies([...selectedCurrencies, currency]);
        }
        setSearchQuery('');
    };

    // Remove currency from selected list
    const handleRemoveCurrency = (currency: string) => {
        setSelectedCurrencies(selectedCurrencies.filter(c => c !== currency));
    };

    if (loading) {
        return (
            <Container maxWidth="lg">
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {locationDetected && (
                <Alert severity="info" sx={{ mb: 3 }}>
                    {t('location.detected')}
                </Alert>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {/* Currency Exchange Form */}
                <CurrencyExchangeForm
                    amount={amount}
                    fromCurrency={fromCurrency}
                    toCurrency={toCurrency}
                    convertedAmount={convertedAmount}
                    exchangeRates={getEffectiveRates()}
                    lastUpdated={lastUpdated}
                    error={error}
                    availableCurrencies={availableCurrencies}
                    onAmountChange={setAmount}
                    onFromCurrencyChange={handleFromCurrencyChange}
                    onToCurrencyChange={handleToCurrencyChange}
                    onSwapCurrencies={handleSwapCurrencies}
                    onRefresh={handleManualRefresh}
                    isRefreshing={isRefreshing}
                    onRateOverride={handleRateOverride}
                />

                {/* Currency Search and Rates Tables */}
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
                    gap: 3
                }}>
                    {/* Left Column - Current Rates */}
                    <Box>
                        <CurrencySearch
                            searchQuery={searchQuery}
                            selectedCurrencies={selectedCurrencies}
                            availableCurrencies={availableCurrencies}
                            onSearchChange={setSearchQuery}
                            onAddCurrency={handleAddCurrency}
                        />
                        <CurrentRatesTable
                            fromCurrency={fromCurrency}
                            selectedCurrencies={selectedCurrencies}
                            exchangeRates={exchangeRates}
                            isRefreshing={isRefreshing}
                            onRefresh={handleManualRefresh}
                            onRemoveCurrency={handleRemoveCurrency}
                        />
                    </Box>

                    {/* Right Column - Reverse Rates */}
                    <Box>
                        <ReverseRatesTable
                            fromCurrency={fromCurrency}
                            selectedCurrencies={selectedCurrencies}
                            exchangeRates={exchangeRates}
                            isRefreshing={isRefreshing}
                            onRefresh={handleManualRefresh}
                        />
                    </Box>
                </Box>
            </Box>
        </Container>
    );
};

export default GuestDashboard; 